import React, { FunctionComponent, memo, useEffect, useState } from 'react'
import Icon from 'blocks.simple/icon/icon'
import { SwitchButton } from 'atoms/SwitchButton'
import { Button } from 'molecules/Button'
import EditText from 'blocks.simple/editText/editText'
import { SecondFactorActionModal } from 'organisms/SecondFactorAction'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { isExist, isNotEmptyString, trimSegmentsFromString } from 'core/utils/coreUtil'
import { authenticationSettingsStyles as styles } from './AuthenticationSettings-styles'
import { useTwoFactorSettings } from './TwoFactorSettings-hooks'

export interface TwoFactorEmailConfirmProps {
    prefix: string | null
    resendLeft: number
    isSendingEmail: boolean
    code: string
    onChangeCode: (value: string) => void
    onSendConfirmationEmail: () => void
    onSendConfirmationEmailCode: (code: string) => void
}

interface TwoFactorSettingsProps {
    twoFactorAuth: 'on' | 'off'
    isEmailConfirmed: boolean
    totpEnabled?: boolean
    maskedEmail?: string
    emailConfirm?: TwoFactorEmailConfirmProps
}

const formatBoundAt = (value?: string | number | null) => {
    if (value === undefined || value === null || value === '') {
        return null
    }
    const raw = typeof value === 'number' ? (value < 1e12 ? value * 1000 : value) : value
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) {
        return null
    }
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    return `${dd}.${mm}.${date.getFullYear()}`
}

const TwoFactorSettingsComponent: FunctionComponent<TwoFactorSettingsProps> = ({
    twoFactorAuth,
    isEmailConfirmed,
    totpEnabled = false,
    maskedEmail,
    emailConfirm,
}) => {
    const {
        status,
        enabled,
        selectMethod,
        setEnabled,
        unbindTotp,
        regenerateRecoveryCodes,
        flow,
    } = useTwoFactorSettings({
        isEmailConfirmed,
        legacyTwoFactorAuth: twoFactorAuth,
        legacyTotpEnabled: totpEnabled,
    })

    const [emailConfirmOpen, setEmailConfirmOpen] = useState(false)
    const [pendingEmailConnect, setPendingEmailConnect] = useState(false)

    useEffect(() => {
        if (!isEmailConfirmed || !pendingEmailConnect) {
            return
        }
        setPendingEmailConnect(false)
        setEmailConfirmOpen(false)
        selectMethod('email')
    }, [isEmailConfirmed, pendingEmailConnect, selectMethod])

    useEffect(() => {
        if (isEmailConfirmed || !enabled) {
            setEmailConfirmOpen(false)
        }
        if (!enabled) {
            setPendingEmailConnect(false)
        }
    }, [isEmailConfirmed, enabled])

    const emailConnected = status.method === 'email'
    const totpConnected = status.method === 'totp' && status.authenticatorBound
    const totpBoundAt = totpConnected ? formatBoundAt(status.authenticatorBoundAt) : null
    const recoveryLeft = status.recoveryCodesRemaining
    const emailDesc = maskedEmail
        ? translate('secondFactorMethodEmailDescWithEmail').replace('{{email}}', maskedEmail)
        : translate('secondFactorMethodEmailDesc')

    const awaitingEmailCode = !!emailConfirm?.prefix && emailConfirmOpen && !isEmailConfirmed

    const getRawCodeString = (value: string) => {
        const prefix = emailConfirm?.prefix
        return trimSegmentsFromString(value, prefix ? [`${prefix}-`, '_'] : ['_'])
    }

    const handleConnectEmail = () => {
        if (!enabled || flow.isBusy) {
            return
        }
        if (isEmailConfirmed) {
            selectMethod('email')
            return
        }
        setPendingEmailConnect(true)
        setEmailConfirmOpen(true)
        emailConfirm?.onSendConfirmationEmail()
    }

    const handleChangeEmailCode = (next: string) => {
        emailConfirm?.onChangeCode(getRawCodeString(next))
    }

    const handleSubmitEmailCode = () => {
        if (!emailConfirm) {
            return
        }
        const raw = getRawCodeString(emailConfirm.code)
        if (!isExist(raw) || !isNotEmptyString(raw)) {
            return
        }
        emailConfirm.onSendConfirmationEmailCode(raw)
    }

    const canSubmitEmailCode =
        !!emailConfirm?.code && isNotEmptyString(getRawCodeString(emailConfirm.code)) && !emailConfirm.isSendingEmail

    return (
        <div className={styles.totpSection}>
            <div className={styles.toggleRow}>
                <SwitchButton
                    disabled={flow.isBusy}
                    checked={enabled}
                    onChange={() => setEnabled(!enabled)}
                    className={`test-set-toggle_${enabled ? 'enabled' : 'disabled'}`}
                />
                <div className={styles.toggleLabel}>{translate('secondFactorAskCodeOnLogin')}</div>
            </div>

            <fieldset disabled={!enabled} className={styles.methodsFieldset}>
                <legend className={styles.methodsLegend}>{translate('secondFactorMethodLegend')}</legend>

                <div className={styles.methodCards}>
                    <div className={styles.methodCard}>
                        <div className={styles.methodCardHeader}>
                            <div className={styles.methodCardTitleRow}>
                                <div className={cn(styles.methodIcon, emailConnected && styles.methodIconActive)}>
                                    <Icon
                                        type="mail"
                                        size={20}
                                        color={emailConnected ? '#286cf2' : 'rgba(26,26,35,0.84)'}
                                    />
                                </div>
                                <div className={styles.methodTitle}>{translate('secondFactorMethodEmail')}</div>
                                {emailConnected ? (
                                    <span className={styles.connectedBadge}>{translate('secondFactorConnected')}</span>
                                ) : null}
                            </div>
                        </div>
                        <div className={styles.methodDesc}>{emailDesc}</div>

                        {!emailConnected ? (
                            <div className={styles.emailCardActions}>
                                {!awaitingEmailCode ? (
                                    <button
                                        type="button"
                                        className={styles.methodBtnToned}
                                        disabled={!enabled || flow.isBusy || emailConfirm?.isSendingEmail}
                                        onClick={handleConnectEmail}
                                    >
                                        {translate('secondFactorConnect')}
                                    </button>
                                ) : null}

                                {awaitingEmailCode && emailConfirm ? (
                                    <>
                                        <div className={styles.gateCodeRow}>
                                            <EditText
                                                containerClass={styles.gateInput}
                                                onChange={handleChangeEmailCode}
                                                placeholder={translate('confirmEmailCodePlaceholder')}
                                                mask={emailConfirm.prefix ? `${emailConfirm.prefix}-****` : undefined}
                                            />
                                            <Button
                                                className={styles.gateSendBtn}
                                                size="m"
                                                mod="fillBrand"
                                                onClick={handleSubmitEmailCode}
                                                disabled={!canSubmitEmailCode}
                                            >
                                                {translate('send')}
                                            </Button>
                                        </div>
                                        <div className={styles.gateResendRow}>
                                            {emailConfirm.resendLeft > 0 ? (
                                                <>
                                                    <span className={styles.gateResendMuted}>
                                                        {translate('secondFactorResendCode')}
                                                    </span>
                                                    <span className={styles.gateResendTimer}>
                                                        {translate('secondFactorResendIn').replace(
                                                            '{{seconds}}',
                                                            String(emailConfirm.resendLeft)
                                                        )}
                                                    </span>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={styles.gateResendLink}
                                                    onClick={() => emailConfirm.onSendConfirmationEmail()}
                                                    disabled={emailConfirm.isSendingEmail}
                                                >
                                                    {translate('secondFactorResendCode')}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                ) : null}

                                {isEmailConfirmed ? (
                                    <div className={styles.emailConfirmedRow}>
                                        <Icon type="circle_checked" size={16} color="#1a1a23" />
                                        <span>{translate('confirmEmailSuccessTitle')}</span>
                                    </div>
                                ) : null}
                            </div>
                        ) : isEmailConfirmed ? (
                            <div className={styles.emailConfirmedRow}>
                                <Icon type="circle_checked" size={16} color="#1a1a23" />
                                <span>{translate('confirmEmailSuccessTitle')}</span>
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.methodCard}>
                        <div className={styles.methodCardHeader}>
                            <div className={styles.methodCardTitleRow}>
                                <div className={cn(styles.methodIcon, totpConnected && styles.methodIconActive)}>
                                    <Icon
                                        type="mobilePhone"
                                        size={20}
                                        color={totpConnected ? '#286cf2' : 'rgba(26,26,35,0.84)'}
                                    />
                                </div>
                                <div className={styles.methodTitle}>{translate('secondFactorMethodTotp')}</div>
                                {totpConnected ? (
                                    <span className={styles.connectedBadge}>{translate('secondFactorConnected')}</span>
                                ) : null}
                            </div>
                            <span className={styles.secureTag}>{translate('secondFactorMethodTotpTag')}</span>
                        </div>
                        <div className={styles.methodDesc}>{translate('secondFactorMethodTotpDesc')}</div>
                        {totpConnected ? (
                            <>
                                <div className={styles.methodActions}>
                                    <button
                                        type="button"
                                        className={styles.methodBtnOutline}
                                        disabled={flow.isBusy}
                                        onClick={() => selectMethod('totp')}
                                    >
                                        {translate('totpRebind')}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.methodBtnDanger}
                                        disabled={flow.isBusy}
                                        onClick={unbindTotp}
                                    >
                                        {translate('totpUnbind')}
                                    </button>
                                </div>
                                {totpBoundAt ? (
                                    <div className={styles.methodBoundAt}>
                                        {translate('totpBoundAt').replace('{{date}}', totpBoundAt)}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <button
                                type="button"
                                className={styles.methodBtnToned}
                                disabled={!enabled || flow.isBusy}
                                onClick={() => selectMethod('totp')}
                            >
                                {translate('secondFactorConnect')}
                            </button>
                        )}
                    </div>
                </div>
            </fieldset>

            {totpConnected ? (
                <div className={styles.backupCodesCard}>
                    <div className={styles.backupCodesInfo}>
                        <div className={styles.backupCodesTitleRow}>
                            <div className={styles.backupCodesIcon}>
                                <Icon type="keys" size={20} color="rgba(26,26,35,0.84)" />
                            </div>
                            <div className={styles.methodTitle}>{translate('totpBackupCodesTitle')}</div>
                        </div>
                        <div className={styles.backupCodesDesc}>
                            {recoveryLeft === null || recoveryLeft === undefined
                                ? translate('totpBackupCodesCardDescFallback')
                                : translate('totpBackupCodesCardDesc')
                                      .replace('{{left}}', String(recoveryLeft))
                                      .replace('{{total}}', '10')}
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.methodBtnOutline}
                        disabled={flow.isBusy}
                        onClick={regenerateRecoveryCodes}
                    >
                        {translate('totpRegenerateBackupCodes')}
                    </button>
                </div>
            ) : null}

            <div className={styles.footerNote}>{translate('secondFactorChangeWarn')}</div>

            {flow.isOpen && flow.step !== 'idle' ? (
                <SecondFactorActionModal
                    open
                    step={flow.step}
                    isBusy={flow.isBusy}
                    errorKey={flow.errorKey}
                    begin={flow.begin}
                    setup={flow.setup}
                    recoveryCodes={flow.recoveryCodes}
                    proofCode={flow.proofCode}
                    totpCode={flow.totpCode}
                    savedAck={flow.savedAck}
                    maskedEmail={maskedEmail}
                    isEmailConfirmed={isEmailConfirmed}
                    pendingAction={flow.pendingAction}
                    onChangeProofCode={flow.setProofCode}
                    onChangeTotpCode={flow.setTotpCode}
                    onChangeSavedAck={flow.setSavedAck}
                    onSubmitProof={flow.submitProof}
                    onResendProof={flow.resendProof}
                    onSubmitTotpSetup={flow.submitTotpSetup}
                    onFinalizeRecoveryCodes={flow.finalizeRecoveryCodes}
                    onCompleteDone={flow.completeDone}
                    onClose={flow.close}
                />
            ) : null}
        </div>
    )
}

export const TwoFactorSettings = memo(TwoFactorSettingsComponent)
